<script lang="ts" generics="S, O">
  import CodeBlock from '../CodeBlock.svelte';
  import VerdictBadge from '../VerdictBadge.svelte';
  import { checkAnswer, replayPlan, type LabModel, type PreparedPuzzle } from '../../lib/labs';
  import FrameBoard from './FrameBoard.svelte';
  import FramePlayer from './FramePlayer.svelte';

  /**
   * 段階 2「予想する」（labs.md 各ラボの手順 3、viz-step カードと同じ題材）。
   * 操作列のコードを読んで最後の print の出力を予想し、答え合わせの後に盤面を 1 ステップずつ見る。
   */
  interface Props {
    model: LabModel<S, O>;
    puzzles: PreparedPuzzle<S>[];
    labels: string[];
  }

  let { model, puzzles, labels }: Props = $props();

  let index = $state(0);
  let input = $state('');
  let graded: boolean | null = $state(null);
  let step = $state(0);

  const puzzle = $derived(puzzles[index]);
  const plan = $derived(puzzle ? replayPlan(model, puzzle.run) : null);
  /** フレーム i を作った行（パズルのコードは宣言 + 操作なので、手 k は宣言の数 + k 行目）。 */
  const declCount = $derived(puzzle ? model.declarations(puzzle.run.initial).length : 0);
  const currentLine = $derived(
    graded === null || !plan ? puzzle?.questionLine : declCount + (plan.frameSteps[step] ?? 0),
  );

  function grade(e: SubmitEvent) {
    e.preventDefault();
    if (!puzzle || input.trim() === '') return;
    graded = checkAnswer(input, puzzle.expected);
    step = 0;
  }

  function choose(i: number) {
    index = i;
    input = '';
    graded = null;
    step = 0;
  }
</script>

<div class="lab predict" data-testid="lab-predict">
  <section class="panel col" aria-labelledby="predict-q-title">
    <h2 id="predict-q-title">問題</h2>
    <div class="choices" role="group" aria-label="問題を選ぶ">
      {#each puzzles as p, i (p.id)}
        <button
          type="button"
          class="btn btn-sm"
          class:btn-primary={i === index}
          aria-pressed={i === index}
          onclick={() => choose(i)}>{i + 1}. {p.title}</button
        >
      {/each}
    </div>
    {#if puzzle}
      <p class="small">次のコードを上から順に実行します。最後の行が出す 1 行を予想してください。</p>
      <CodeBlock code={puzzle.code} label="予想するコード" currentLine={currentLine ?? 0} />
      <form class="answer" onsubmit={grade}>
        <label for="predict-input">最後の行の出力</label>
        <div class="row">
          <input
            id="predict-input"
            type="text"
            autocomplete="off"
            spellcheck="false"
            bind:value={input}
            disabled={graded !== null}
            data-testid="predict-input"
          />
          <button type="submit" class="btn btn-primary" disabled={graded !== null}
            >答え合わせ <kbd>Enter</kbd></button
          >
        </div>
      </form>
      {#if graded !== null}
        <div class="verdict-line" role="status">
          <VerdictBadge verdict={graded ? 'AC' : 'WA'} />
          <span>
            {#if graded}出力が一致しました。{:else}出力は <code>{puzzle.expected}</code> です。{/if}
            盤面を 1 ステップずつ見られます。
          </span>
        </div>
        <button type="button" class="btn btn-sm" onclick={() => choose(index)}>もう一度</button>
      {/if}
    {/if}
  </section>

  <section class="panel col" aria-labelledby="predict-board-title">
    <h2 id="predict-board-title">盤面</h2>
    {#if graded === null || !plan}
      <div class="hidden-board">答え合わせの後に、盤面が 1 手ずつ動くところを見られます。</div>
    {:else}
      <FramePlayer count={plan.frames.length} bind:step label="答えの盤面のステップ操作">
        {#snippet children(i)}
          {@const f = plan.frames[i]}
          {#if f}<FrameBoard state={f.state} {labels} />{/if}
        {/snippet}
      </FramePlayer>
    {/if}
  </section>
</div>

<style>
  .lab {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .col h2 {
    margin: 0;
  }
  .small {
    font-size: var(--fs-sm);
  }
  .choices {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .answer label {
    display: block;
    font-size: var(--fs-sm);
    margin-bottom: var(--space-1);
  }
  .row {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .row input {
    flex: 1;
    min-width: 10rem;
    font-family: var(--font-mono);
  }
  .verdict-line {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .hidden-board {
    display: grid;
    place-items: center;
    min-height: 12rem;
    padding: var(--space-4);
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius-md);
    color: var(--text-soft);
    font-size: var(--fs-sm);
    text-align: center;
  }
  @media (max-width: 1023px) {
    .lab {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
